import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { calculateCardWidth, formatDate } from '../../../components/miscUtils';
import { getAssetTypeIcon, getAssetTypeName, useOpenAsset } from '../../../data/assets/helpers';

import { TpAssetInfo } from '../../../data/assets/interfaces';
import { addNewAsset, setSortBy, setPage } from '../../../data/assets/queriedAssetsSlice';
import { getHighlightedText } from '../../visual-analytics/helpers/misc';
import styles from '../StartPage.module.scss';
import { useRef, useState, useMemo } from 'react';
import useResizeObserver from '../../visual-analytics/hooks/resize-observer';
import { promptCreateAsset } from '../../crud-asset/CrudAsset';
import { useApiContext } from '../../../api';
import { useAppDispatch, useAppSelector, useUser } from '../../../app/hooks';
import { showErrorMessage } from '../../../components/message-boxes/ErrorMessage';
import { selectTemplatesAndCombinedOntology } from '../../../data/templateSlice';
import { useNavigate } from 'react-router-dom';

import { TpSortOptions } from '../StartPageTemplateUtils';
import { Spinner } from '../../../components/primitives/Spinner';

interface TpAssetOverviewProps {
  ontology: any;
  assets: TpAssetInfo[];
  queryStats: any;
  dashboardtemplate: any;
  searchText: string;
  typeFilter: string;
  sortBy: string;
  sortAsc: boolean;
  page: number;
  limit: number;
  fetchAssets: () => void;
  isFetching: boolean;
}

const AssetOverview = (props: TpAssetOverviewProps) => {
  const api = useApiContext();
  const {
    ontology,
    assets,
    queryStats,
    searchText,
    dashboardtemplate,
    typeFilter,
    sortBy,
    sortAsc,
    page,
    limit,
    fetchAssets,
    isFetching,
  } = props;
  const openAsset = useOpenAsset();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { uploadTemplates, assetViews } = useAppSelector(selectTemplatesAndCombinedOntology);
  const { user } = useUser();
  const refAssetGrid = useRef<HTMLDivElement>(null);
  const [cardWidth, setCardWidth] = useState(220);

  // Sort values
  const sortOptions: TpSortOptions[] = dashboardtemplate.sortOptions || [];
  const selectedOption = sortOptions.find((option) => option.assetInfoKey === sortBy && !option.reverse === sortAsc);
  const sortKey = selectedOption ? selectedOption.id : '';

  // Page count
  const pageCount = useMemo(() => {
    let valid_assets_count = queryStats['num_of_items'] ?? 0;
    if (typeFilter) valid_assets_count = queryStats[typeFilter] ?? 0;
    return Math.max(0, Math.ceil(valid_assets_count / (limit || 1)));
  }, [typeFilter, queryStats, limit]);

  useResizeObserver(refAssetGrid, () => {
    calculateCardWidth(refAssetGrid, styles.assetCard, 180, 220, setCardWidth);
  });

  function renderAssetCard(
    asset: TpAssetInfo,
    searchText: string,
    openAsset: any,
    assetOntology: any,
    cardOptions: any,
  ) {
    if (!assetOntology) {
      throw new Error('Asset ontology not found for asset ' + asset.displayName + ' with type ' + asset.type);
    }
    return (
      <div
        className={`${styles.assetCard} ${
          !cardOptions.disableDescription ? styles.assetCardHeightBig : styles.assetCardHeightSmall
        }`}
        key={asset.id}
        style={{ width: cardWidth }}
        onClick={() => {
          openAsset(asset.id);
        }}
      >
        <div className={styles.assetCardHeader}>
          <div className={styles.assetCardIcon}>
            <FontAwesomeIcon icon={['fad', getAssetTypeIcon(assetOntology)]} />
          </div>
          <div className={styles.assetCardType}>{getAssetTypeName(assetOntology)}</div>
        </div>
        <div className={cardOptions.disableDescription ? styles.assetCardNameBig : styles.assetCardNameSmall}>
          {getHighlightedText(asset.displayName, searchText)}
        </div>
        {!cardOptions.disableDescription && (
          <div className={styles.assetCardDescription}>{getHighlightedText(asset.description, searchText)}</div>
        )}

        <div className={styles.assetCardDateLine1}>
          <div className={styles.assetCardLineIcon}>
            <FontAwesomeIcon icon={['fas', 'calendar-days']} />
          </div>
          {formatDate(asset.creationDate)}
        </div>
      </div>
    );
  }

  function renderCreationCard(assetOntology: any, cardOptions: any) {
    return (
      <div
        className={`${styles.createCard} ${
          !cardOptions.disableDescription ? styles.assetCardHeightBig : styles.assetCardHeightSmall
        }`}
        key={`create-${assetOntology.identifier}`}
        style={{ width: cardWidth }}
        onClick={() => {
          const matchingUploads = Object.values(uploadTemplates).find((elem) => {
            return elem.collectionAssetType === assetOntology.identifier;
          });

          if (matchingUploads) {
            navigate(`/upload/${matchingUploads.id}`);
          } else {
            promptCreateAsset(assetOntology, api)
              .then((asset: TpAssetInfo) => {
                dispatch(addNewAsset({ asset }));
                fetchAssets();
              })
              .catch((error) => {
                if (error) {
                  console.error(error);
                  showErrorMessage(
                    'Error Creating new' +
                      assetOntology.identifier +
                      '. \n Please contact your administrator: \n ' +
                      error,
                  );
                }
                return;
              });
          }
        }}
      >
        <div className={styles.assetCardHeader}>
          <div className={styles.assetCardIcon}>
            <FontAwesomeIcon icon={['fad', getAssetTypeIcon(assetOntology)]} />
          </div>
          <div className={styles.assetCardType}>{`Create new ${assetOntology.displayName}`}</div>
        </div>
        <div className={styles.createCardIcon}>
          <FontAwesomeIcon icon={['fas', 'plus']} />
        </div>
      </div>
    );
  }

  function updateSortBy(sortId: string) {
    const sortingOption = sortOptions.find((option) => option.id === sortId) || sortOptions[0];
    if (!sortingOption) {
      // No valid sort option found; do nothing or handle as needed
      return;
    }
    dispatch(setSortBy({ sortBy: sortingOption.assetInfoKey, asc: !sortingOption.reverse }));
  }

  return (
    <>
      {!dashboardtemplate.disableSort && sortOptions.length > 0 && (
        <div className={styles.sortLabel}>
          Sort by:
          <select
            className={styles.sortSelector}
            onChange={(e) => updateSortBy(e.target.value)}
            value={sortKey}
            disabled={isFetching}
          >
            {sortOptions.map((option) => (
              <option key={option.id} value={option.id}>
                {option.title}
              </option>
            ))}
          </select>
        </div>
      )}

      {isFetching && <Spinner />}

      {!isFetching && (
        <div className={styles.assetGrid} ref={refAssetGrid}>
          {typeFilter &&
            user.canWrite &&
            ontology.assetTypes[typeFilter].userCanCreate &&
            !!assetViews[typeFilter] &&
            renderCreationCard(ontology.assetTypes[typeFilter], dashboardtemplate.cardOptions)}
          {assets
            .filter((asset) => !!ontology.assetTypes[asset.type])
            .map((asset) =>
              renderAssetCard(
                asset,
                searchText,
                openAsset,
                ontology.assetTypes[asset.type],
                dashboardtemplate.cardOptions,
              ),
            )}
        </div>
      )}

      {!isFetching && !!pageCount && pageCount > 1 && (
        <div className={styles.sortLabel}>
          Page:
          <select
            className={styles.sortSelector}
            value={page}
            onChange={(e) => dispatch(setPage({ page: Number(e.target.value) }))}
          >
            {Array.from({ length: pageCount }, (_, i) => i).map((option) => (
              <option key={option} value={option}>
                {option + 1}
              </option>
            ))}
          </select>
        </div>
      )}
    </>
  );
};

export default AssetOverview;
